import React, { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import HAStatusIndicator from './HAStatusIndicator';

const SortIndicator = ({ field, currentSort, currentDirection }) => {
  const isActive = currentSort === field;
  const baseClass = "inline-block ml-1";

  return (
    <div className={`${baseClass} flex flex-col -space-y-1`}>
      <svg 
        className={`h-4 w-4 ${
          isActive && currentDirection === 'desc' 
            ? 'text-blue-600' 
            : isActive 
              ? 'text-gray-200' 
              : 'text-gray-400'
        }`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M5 14l7-7 7 7z" />
      </svg>
      <svg 
        className={`h-4 w-4 ${
          isActive && currentDirection === 'asc' 
            ? 'text-blue-600' 
            : isActive 
              ? 'text-gray-200' 
              : 'text-gray-400'
        }`}
        fill="currentColor" 
        viewBox="0 0 24 24"
      >
        <path d="M5 10l7 7 7-7z" />
      </svg>
    </div>
  );
};

/** Extract originStreamId from Phenix member stream URI. Matches backend logic. Strips query string (e.g. ?capabilities=...). */
function extractStreamIdsFromMembers(membersResponse) {
  const ids = [];
  const seen = new Set();
  for (const member of membersResponse.members || []) {
    for (const stream of member.streams || []) {
      const uri = stream?.uri;
      if (!uri || typeof uri !== 'string') continue;
      const match = uri.match(/pcast:\/\/[^/]+\/(.+)$/);
      const path = match ? match[1] : null;
      const id = path ? path.split('?')[0] : null;
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
  }
  return ids;
}

const MEMBER_REFRESH_INTERVAL_MS = 15000; // Same as dashboard-refresh on viewer page

const MemberDetailsContext = React.createContext();

const PublisherStatus = ({ channelId, applicationId, applicationSecret, expectedStreams }) => {
  const [state, setState] = useState('loading');
  const [memberCount, setMemberCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [streamIds, setStreamIds] = useState([]);
  const updateMemberDetails = useContext(MemberDetailsContext);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const authToken = btoa(`${applicationId}:${applicationSecret}`);
        const response = await fetch('/api/channel-members', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${authToken}`
          },
          body: JSON.stringify({ channelId })
        });

        if (!response.ok) throw new Error('Members fetch failed');

        const data = await response.json();
        const members = data.members || [];
        const activeMembers = members.filter(m => m.state === 'Active');
        const ids = extractStreamIdsFromMembers(data);

        setMemberCount(members.length);
        setActiveCount(activeMembers.length);
        setStreamIds(ids);
        setState('ready');

        updateMemberDetails(prev => new Map(prev).set(channelId, {
          memberCount: members.length,
          activeCount: activeMembers.length,
          streamIds: ids,
          state: 'ready'
        }));
      } catch (error) {
        console.error('Channel members error:', error);
        setState('error');
        updateMemberDetails(prev => new Map(prev).set(channelId, {
          memberCount: 0,
          activeCount: 0,
          streamIds: [],
          state: 'error'
        }));
      }
    };

    fetchMembers();
    const intervalId = setInterval(fetchMembers, MEMBER_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [channelId, applicationId, applicationSecret, updateMemberDetails]);

  return (
    <HAStatusIndicator
      memberCount={memberCount}
      activeCount={activeCount}
      expectedStreams={expectedStreams}
      state={state}
    />
  );
};

const ChannelList = ({ channels, selectedChannels = [], onChannelSelect, applicationId, applicationSecret }) => {
  const [sortField, setSortField] = useState('alias');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterText, setFilterText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [memberDetails, setMemberDetails] = useState(new Map());
  const [expectedStreamsMap, setExpectedStreamsMap] = useState(new Map());

  const getChannelId = (channel) => channel.channelId;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Status: first click shows channels with publishers on top (desc)
      setSortDirection(field === 'status' ? 'desc' : 'asc');
    }
  };

  const enrichChannel = useCallback((ch, map) => {
    const cid = getChannelId(ch);
    const details = memberDetails.get(cid);
    const exp = map.get(cid) ?? (details?.memberCount ? Math.min(2, details.memberCount) : 1);
    const streamIds = exp >= 2 && details?.streamIds?.length ? details.streamIds.slice(0, 2) : undefined;
    return { ...ch, expectedStreams: exp, streamIds };
  }, [memberDetails]);

  const buildSelectedChannelsWithMap = useCallback((ids, expMap) => {
    const map = expMap ?? expectedStreamsMap;
    // Preserve existing selection order, append new channels at the end
    const existing = selectedChannels
      .filter(ch => ids.has(getChannelId(ch)))
      .map(ch => enrichChannel(ch, map));
    const existingIds = new Set(existing.map(ch => getChannelId(ch)));
    const added = channels
      .filter(ch => ids.has(getChannelId(ch)) && !existingIds.has(getChannelId(ch)))
      .map(ch => enrichChannel(ch, map));
    return [...existing, ...added];
  }, [channels, selectedChannels, memberDetails, expectedStreamsMap, enrichChannel]);

  const handleCheckboxChange = (channel) => {
    const channelId = getChannelId(channel);
    const newSelectedIds = new Set(selectedIds);
    let newExpMap = expectedStreamsMap;

    if (newSelectedIds.has(channelId)) {
      newSelectedIds.delete(channelId);
    } else {
      newSelectedIds.add(channelId);
      const details = memberDetails.get(channelId);
      if (details && !expectedStreamsMap.has(channelId)) {
        const suggested = details.memberCount ? Math.min(2, details.memberCount) : 1;
        newExpMap = new Map(expectedStreamsMap).set(channelId, suggested);
        setExpectedStreamsMap(newExpMap);
      }
    }

    setSelectedIds(newSelectedIds);
    onChannelSelect(buildSelectedChannelsWithMap(newSelectedIds, newExpMap));
  };

  const handleExpectedStreamsChange = (channelId, value) => {
    const num = parseInt(value, 10);
    if (num !== 1 && num !== 2) return;
    const newMap = new Map(expectedStreamsMap).set(channelId, num);
    setExpectedStreamsMap(newMap);
    onChannelSelect(buildSelectedChannelsWithMap(selectedIds, newMap));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return '';
    }
  };

  const getPublisherLabel = (channelId) => {
    const details = memberDetails.get(channelId);
    if (!details || details.state === 'loading') return '—';
    if (details.state === 'error') return 'Error';
    const { memberCount, activeCount } = details;
    if (memberCount === 0) return '0 active';
    if (activeCount === memberCount) return `${activeCount} active`;
    return `${activeCount} of ${memberCount}`;
  };

  const filteredAndSortedChannels = useMemo(() => {
    return channels
      .filter(channel => {
        const searchText = filterText.toLowerCase();
        return (
          channel.alias?.toLowerCase().includes(searchText) ||
          channel.name?.toLowerCase().includes(searchText) ||
          channel.description?.toLowerCase().includes(searchText)
        );
      })
      .sort((a, b) => {
        if (sortField === 'status') {
          const detailsA = memberDetails.get(getChannelId(a));
          const detailsB = memberDetails.get(getChannelId(b));
          const activeA = detailsA?.activeCount ?? -1;
          const activeB = detailsB?.activeCount ?? -1;
          const compareResult = activeA - activeB;
          return sortDirection === 'asc' ? compareResult : -compareResult;
        }
        if (sortField === 'publishers') {
          const detailsA = memberDetails.get(getChannelId(a));
          const detailsB = memberDetails.get(getChannelId(b));
          const countA = detailsA?.memberCount ?? -1;
          const countB = detailsB?.memberCount ?? -1;
          const compareResult = countA - countB;
          return sortDirection === 'asc' ? compareResult : -compareResult;
        }
        const aValue = a[sortField] || '';
        const bValue = b[sortField] || '';
        const compareResult = (aValue + '').localeCompare(bValue + '');
        return sortDirection === 'asc' ? compareResult : -compareResult;
      });
  }, [channels, filterText, sortField, sortDirection, memberDetails]);

  return (
    <MemberDetailsContext.Provider value={setMemberDetails}>
      <div className="mx-auto">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Filter channels..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-auto">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left"></th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIndicator field="status" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('alias')}
                >
                  <div className="flex items-center">
                    Channel Alias
                    <SortIndicator field="alias" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('publishers')}
                >
                  <div className="flex items-center">
                    Publishers
                    <SortIndicator field="publishers" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
                <th className="px-4 py-2 text-left">Expected</th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    Channel Name
                    <SortIndicator field="name" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('description')}
                >
                  <div className="flex items-center">
                    Description
                    <SortIndicator field="description" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created')}
                >
                  <div className="flex items-center">
                    Created
                    <SortIndicator field="created" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('lastUpdate')}
                >
                  <div className="flex items-center">
                    Modified
                    <SortIndicator field="lastUpdate" currentSort={sortField} currentDirection={sortDirection} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedChannels.map(channel => {
                const channelId = getChannelId(channel);
                const details = memberDetails.get(channelId);
                const expectedStreams = expectedStreamsMap.get(channelId)
                  ?? (details?.memberCount ? Math.min(2, details.memberCount) : 1);
                const isSelected = selectedIds.has(channelId);

                return (
                  <tr key={channelId} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(channel)}
                        className="mr-2"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <PublisherStatus
                        channelId={channelId}
                        applicationId={applicationId}
                        applicationSecret={applicationSecret}
                        expectedStreams={expectedStreams}
                      />
                    </td>
                    <td className="px-4 py-2">{channel.alias}</td>
                    <td className="px-4 py-2">{getPublisherLabel(channelId)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`expected-${channelId}`}
                            value="1"
                            checked={expectedStreams === 1}
                            onChange={(e) => handleExpectedStreamsChange(channelId, e.target.value)}
                            className="mr-1"
                          />
                          <span>1</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`expected-${channelId}`}
                            value="2"
                            checked={expectedStreams === 2}
                            onChange={(e) => handleExpectedStreamsChange(channelId, e.target.value)}
                            className="mr-1"
                          />
                          <span>2</span>
                        </label>
                      </div>
                    </td>
                    <td className="px-4 py-2">{channel.name}</td>
                    <td className="px-4 py-2">{channel.description}</td>
                    <td className="px-4 py-2">{formatDate(channel.created)}</td>
                    <td className="px-4 py-2">{formatDate(channel.lastUpdate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </MemberDetailsContext.Provider>
  );
};

export default ChannelList;
